/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Row,
  Col,
  Spin,
  Card,
  Button,
  message,
  Input,
  DatePicker,
  Typography,
} from 'antd';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import getFormattedValues, { DATE_FRONTEND_FORMAT, discartNullValues } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import { InputSearchPro } from 'components/InputSearch';
import Select from 'components/Select';
import BusquedaAvanzadaPredio from 'views/Recaudacion/Caja/BusquedaAvanzadaPadrones';
import { isMutable } from 'utils/estadosGlobales';
import { ActionsNames, getModelPermission, hasActionPermission } from 'utils/permission/index';

import {
  getHospedajes,
  getHospedaje,
  postHospedaje,
  patchHospedaje,
  deleteHospedaje,
  baseURI as baseURIHospedaje,
} from 'api/empresas/hospedajes';
import {
  getArrendamientos,
  getArrendamiento,
  postArrendamiento,
  patchArrendamiento,
  deleteArrendamiento,
  baseURI as baseURIArrendamiento,
} from 'api/empresas/arrendamientos';
import {
  getNominas,
  getNomina,
  postNomina,
  patchNomina,
  deleteNomina,
  baseURI as baseURINomina,
} from 'api/empresas/nomina';
import {
  getAlcoholes,
  getAlcohol,
  postAlcohol,
  patchAlcohol,
  deleteAlcohol,
  baseURI as baseURIAlcohol,
} from 'api/empresas/alcoholes';
import {
  getCedulas,
  getCedula,
  postCedula,
  patchCedula,
  deleteCedula,
  baseURI as baseURICedula,
} from 'api/empresas/cedulas';
import {
  getEstacionamientos,
  getEstacionamiento,
  postEstacionamiento,
  patchEstacionamiento,
  deleteEstacionamiento,
  baseURI as baseURIEstacionamiento,
} from 'api/empresas/estacionamientos';
import {
  getJuegosDeAzar,
  getJuegoDeAzar,
  postJuegoDeAzar,
  patchJuegoDeAzar,
  deleteJuegoDeAzar,
  baseURI as baseURIJuegoDeAzar,
} from 'api/empresas/juegosDeAzar';
import {
  getNotarios,
  getNotario,
  postNotario,
  patchNotario,
  deleteNotario,
  baseURI as baseURINotario,
} from 'api/empresas/notarios';
import {
  getCasasDeEmpenio,
  getCasaDeEmpenio,
  postCasaDeEmpenio,
  patchCasaDeEmpenio,
  deleteCasaDeEmpenio,
  baseURI as baseURICasaDeEmpenio,
} from 'api/empresas/casasDeEmpenio';
import {
  getAgencias,
  getAgencia,
  postAgencia,
  patchAgencia,
  deleteAgencia,
  baseURI as baseURIAgencia,
} from 'api/empresas/agencias';
import {
  getRetenedorCedula,
  getRetenedoresDeCedulas,
  patchRetenedorDeCedula,
  postRetenedorDeCedula,
  baseURI as baseURIRetenedorCedular,
  deleteRetenedorDeCedula,
} from 'api/empresas/retenedorDeCedulas';
import {
  getRetenedorDeNomina,
  getRetenedoresDeNominas,
  patchRetenedorDeNomina,
  postRetenedorDeNomina,
  deleteRetenedorDeNomina,
  baseURI as baseURIRetenedorNomina,
} from 'api/empresas/retenedorDeNomina';

import { getEmpresas, getEmpresa } from 'api/empresas/empresas';
import { getUnidadesDeRecaudacion } from 'api/configuracion/unidadesDeRecaudacion';
import { getTiposDeAlcoholes } from 'api/empresas/tiposDeAlcoholes';
import { capitalizeFirstLetter } from 'utils/URIFormat';
import {
  getVendedorDeAlcoholes,
  getVendedoresDeAlcoholes,
  patchVendedorDeAlcoholes,
  postVendedorDeAlcoholes,
  deleteVendedorDeAlcoholes,
  baseURI as baseURIVendedorDeAlcohol,
} from 'api/empresas/ventaDeAlcoholes';
// import { getGirosDeAlcoholes } from 'api/recaudacion/girosDeAlcoholes';
import { getGirosDeAlcoholes } from 'api/recaudacion/girosDeAlcoholes';
import { capitalize } from 'utils/normalizers';
import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import NumericInput from './NumericInput';
import EmpresaDePadronMultiple, {
  catalogsModels as catalogsModelsEmpresas,
  model as empresasModel,
} from './EmpresasPadronMultiple';
import ContribuyenteDeEmpresas from './ContribuyenteDeEmpresas';

const { Text, Title } = Typography;

const models = ['unidadderecaudacion', 'empresa'];

const cruds = {
  hospedaje: {
    baseURI: baseURIHospedaje,
    delete: deleteHospedaje,
    get: getHospedajes,
    getByID: getHospedaje,
    patch: patchHospedaje,
    post: postHospedaje,
  },
  'cedular-por-arrendamiento': {
    get: getArrendamientos,
    getByID: getArrendamiento,
    post: postArrendamiento,
    patch: patchArrendamiento,
    delete: deleteArrendamiento,
    baseURI: baseURIArrendamiento,
  },
  nomina: {
    get: getNominas,
    getByID: getNomina,
    post: postNomina,
    patch: patchNomina,
    delete: deleteNomina,
    baseURI: baseURINomina,
  },
  alcohol: {
    get: getAlcoholes,
    getByID: getAlcohol,
    post: postAlcohol,
    patch: patchAlcohol,
    delete: deleteAlcohol,
    baseURI: baseURIAlcohol,
  },
  'cedular-por-servicios-profesionales': {
    get: getCedulas,
    getByID: getCedula,
    post: postCedula,
    patch: patchCedula,
    delete: deleteCedula,
    baseURI: baseURICedula,
  },
  'juegos-de-azar': {
    get: getJuegosDeAzar,
    getByID: getJuegoDeAzar,
    post: postJuegoDeAzar,
    patch: patchJuegoDeAzar,
    delete: deleteJuegoDeAzar,
    baseURI: baseURIJuegoDeAzar,
  },
  notario: {
    get: getNotarios,
    getByID: getNotario,
    post: postNotario,
    patch: patchNotario,
    delete: deleteNotario,
    baseURI: baseURINotario,
  },
  'casa-de-empeno': {
    get: getCasasDeEmpenio,
    getByID: getCasaDeEmpenio,
    post: postCasaDeEmpenio,
    patch: patchCasaDeEmpenio,
    delete: deleteCasaDeEmpenio,
    baseURI: baseURICasaDeEmpenio,
  },
  agencia: {
    get: getAgencias,
    getByID: getAgencia,
    post: postAgencia,
    patch: patchAgencia,
    delete: deleteAgencia,
    baseURI: baseURIAgencia,
  },
  estacionamiento: {
    get: getEstacionamientos,
    getByID: getEstacionamiento,
    post: postEstacionamiento,
    patch: patchEstacionamiento,
    delete: deleteEstacionamiento,
    baseURI: baseURIEstacionamiento,
  },
  'retenedor-de-cedular': {
    get: getRetenedoresDeCedulas,
    getByID: getRetenedorCedula,
    post: postRetenedorDeCedula,
    patch: patchRetenedorDeCedula,
    delete: deleteRetenedorDeCedula,
    baseURI: baseURIRetenedorCedular,
  },
  'retenedor-de-nomina': {
    get: getRetenedoresDeNominas,
    getByID: getRetenedorDeNomina,
    post: postRetenedorDeNomina,
    patch: patchRetenedorDeNomina,
    delete: deleteRetenedorDeNomina,
    baseURI: baseURIRetenedorNomina,
  },
  'vendedor-de-alcoholes': {
    get: getVendedoresDeAlcoholes,
    getByID: getVendedorDeAlcoholes,
    post: postVendedorDeAlcoholes,
    patch: patchVendedorDeAlcoholes,
    delete: deleteVendedorDeAlcoholes,
    baseURI: baseURIVendedorDeAlcohol,
  },
};

const PadronMultiple = ({
  permission,
}) => {
  const {
    estadosGlobales,
  } = useSelector((state) => state.catalogs);
  const [form] = Form.useForm();
  const [formSearch] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState({ results: [] });
  const [mutable, setMutable] = useState(true);
  const [visibleBusqueda, setVisibleBusqueda] = useState(false);
  const [dataExterno, setDataExterno] = useState([]);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [empresaFound, setEmpresaFound] = useState([]);
  const [unidadesDeRecaudacion, setUnidadesDeRecaudacion] = useState([]);
  const [tiposDeAlcoholes, setTiposDeAlcoholes] = useState([]);
  // const [actividadEmpresarial, setActividadEmpresarial] = useState([]);
  const currentURI = window.location.href.split('/').pop();
  const displayTitle = currentURI.replaceAll('-', ' ');
  const [empresasPermission, setEmpresasPermission] = useState();
  const [girosDeAlcoholes, setGirosDeAlcoholes] = useState([]);
  const [justView, setJustView] = useState(false);

  const currentComponent = cruds[currentURI];

  const fetchData = async (page = 1, q = null) => {
    setLoading(true);
    setCurrentPage(page);
    const _data = await currentComponent.get({ page, ...q });
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    let _hasAllPermission = true;
    const validatePermission = () => {
      const _hasAllViewPermission = models
        .map((m) => hasActionPermission(m, ActionsNames.view))
        .every((e) => e);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        const _empresasPermission = getModelPermission(
          empresasModel,
          catalogsModelsEmpresas,
          true,
        );
        _hasAllPermission = _hasAllViewPermission;
        setHasAllViewPermission(_hasAllViewPermission);
        setEmpresasPermission(_empresasPermission);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const _unidadesDeRecaudacion = await getUnidadesDeRecaudacion();
      const _tiposDeAlcoholes = await getTiposDeAlcoholes();
      // const _actividadesEmpresariales = await getClasesDeActividadesEmpresariales();
      if (mounted) {
        setUnidadesDeRecaudacion(_unidadesDeRecaudacion);
        setTiposDeAlcoholes(_tiposDeAlcoholes);
        // setActividadEmpresarial(_actividadesEmpresariales);
        setLoading(false);
      }
    };
    validatePermission();
    if (_hasAllPermission) {
      fetchAll();
    }
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setMutable(true);
    setEmpresaFound([]);
    setGirosDeAlcoholes([]);
    setData({ results: [] });
    setJustView(false);
  };
  const fetchEmpresa = async (id) => {
    try {
      setLoading(true);
      if (id) {
        const _empresa = await getEmpresa(id?.id || id);
        if (_empresa) {
          setTimeout(() => {
            form.setFieldsValue({
              empresa: _empresa.id,
            });
          });
          setEmpresaFound({
            ..._empresa,
            nombre_comercial: `${_empresa?.clave} - ${
              (_empresa?.nombre_comercial && _empresa?.nombre_comercial.trim() !== '')
                ? _empresa?.nombre_comercial
                : _empresa?.ciudadano?.nombre_completo
    || _empresa?.contribuyente?.razon_social
            }`,
          });
        } else {
          message.info('No se encontro la empresa');
        }
        setLoading(false);
      }
    } catch (error) {
      onError(error, setLoading);
    }
  };

  const filterGiros = async (tipo_de_alcohol) => {
    if (!tipo_de_alcohol) {
      return;
    }
    try {
      setLoading(true);
      if (tipo_de_alcohol) {
        form.resetFields(['giro_de_alcohol']);
        const giros = await getGirosDeAlcoholes({ tipo_de_alcohol });
        setGirosDeAlcoholes(giros);
      } else {
        setGirosDeAlcoholes([]);
      }
      setLoading(false);
    } catch (error) {
      onError(error);
    }
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || await currentComponent.getByID(key);
    setTimeout(() => {
      if (match) {
        filterGiros(match?.tipo_de_alcohol);
        const { empresa } = match;
        setEmpresaFound({
          ...empresa,
          nombre_comercial: `${empresa?.clave} - ${
            (empresa?.nombre_comercial && empresa?.nombre_comercial.trim() !== '')
              ? empresa?.nombre_comercial
              : empresa?.ciudadano?.nombre_completo
  || empresa?.contribuyente?.razon_social
          }`,
        });
        setMutable(isMutable(match.estados_globales));
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue({
            ...match,
            empresa: match?.empresa?.id,
          });

          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const formattedValues = getFormattedValues(values);
      const [key] = selectedRowKeys;
      const _data = await (key
        ? currentComponent.patch(key, formattedValues) : currentComponent.post(formattedValues));
      if (_data) {
        if (_continue) {
          if (!key) {
            setSelectedRowKeys([_data.id]);
            setTimeout(() => onClickEdit(_data));
          } else {
            setTimeout(() => onClickEdit());
          }
        } else {
          onCancel();
        }
      }
      setLoading(false);
      return true;
    } catch (err) {
      onError(err, setLoading);
      return false;
    }
  };

  const deleteItem = async () => {
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await currentComponent.delete(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
      }
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.resetFields();
    setTimeout(() => {
      const values = discartNullValues(record);
      form.setFieldsValue(values);
    });
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Clave',
      key: 'clave',
      dataIndex: 'clave',
      width: 350,
    },
    {
      titleText: 'Descripcíon',
      key: 'descripcion',
      dataIndex: 'descripcion',
      width: 350,
    },
    {
      titleText: 'Unidad Recaudadora',
      dataIndex: 'unidad_recaudadora',
      key: 'unidad_recaudadora',
      width: 300,
      catalog: {
        cat: unidadesDeRecaudacion,
        labelProp: 'descripcion',
        use: false,
      },
    },
  ];

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const resetAndValidate = () => {
    setEmpresaFound([]);
    form.resetFields(['q']);
  };

  // const _data = await currentComponent.get({ page: 1 });
  const searchEmpresa = async () => {
    try {
      setLoading(true);
      await form.validateFields(['q']);
      const { q } = form.getFieldsValue(['q']);
      if (q) {
        const _empresas = await getEmpresas({ q });
        if (_empresas.results.length) {
          const [selected] = _empresas.results;
          form.setFieldsValue({
            empresa: selected.id,
          });
          setEmpresaFound([{
            ...selected,
            nombre_comercial: `${selected?.clave} - ${selected?.nombre_comercial
              || selected?.ciudadano?.nombre_completo
              || selected?.contribuyente?.razon_social}`,
          }]);
        } else if (!_empresas.results.length) {
          message.info('No se encontró la empresa');
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const rules = { required: [{ required: true }] };

  const handleSearch = async () => {
    try {
      setLoading(true);
      await formSearch.validateFields();
      const params = formSearch.getFieldsValue();
      const keys = Object.keys(params);
      const validProps = keys.filter((prop) => params[prop]);
      if (validProps?.length) {
        const _data = await currentComponent.get(params);
        setData(_data);
      } else {
        message.warn('Debe llenar al menos un campo.');
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const wipe = () => {
    formSearch.resetFields();
    setData({ results: [] });
  };

  return (
    <Row align="center" justify="center" className="container">
      <Col xs={24} sm={24} md={24}>
        <Spin tip="Cargando..." spinning={loading}>
          {!visible ? (
            <>
              <Form
                layout="vertical"
                form={formSearch}
                scrollToFirstError
                onFinish={() => handleSearch()}
              >
                <Row gutter={10}>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      label="clave"
                      name="clave"
                      normalize={capitalize}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="descripcion"
                      label="Descripción"
                      normalize={capitalize}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  {/* <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      label="RFC"
                      name="rfc"
                      rules={rules.RFC}
                      normalize={(value) => value.toUpperCase()}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      label="CURP"
                      name="curp"
                      normalize={capitalize}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col> */}
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      label="unidad recaudadora"
                      name="unidad_recaudadora"
                    >
                      <Select dataSource={unidadesDeRecaudacion} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={4}>
                    <Button
                      onClick={handleSearch}
                      block
                      style={{ marginTop: 21 }}
                    >
                      <SearchOutlined />
                      <Typography.Text>
                        Buscar
                      </Typography.Text>
                    </Button>
                  </Col>
                  <Col xs={24} sm={24} md={4}>
                    <Button
                      onClick={wipe}
                      block
                      style={{ marginTop: 21 }}
                    >
                      <DeleteOutlined />
                      <Typography.Text>
                        Limpiar Busqueda
                      </Typography.Text>
                    </Button>
                  </Col>
                </Row>
              </Form>
              <Table
                cols={columns}
                data={data?.results}
                rowSelection={rowSelection}
                handleOnRowClick={handleOnRowClick}
                controls={{
                  onClickAdd,
                  onClickEdit: () => onClickEdit(null),
                  onClickDelete,
                  onClickExpand: () => {
                    setJustView(true);
                    onClickEdit(null);
                  },
                }}
                allowExpand
                rowKey="id"
                permission={permission}
                pagination={false}
                paginationCallback={fetchData}
                count={data?.count}
                currentPage={currentPage}
                hasAllViewPermission={hasAllViewPermission}
              />
            </>
          ) : (
            <Card
              className="form-container no-padding"
              title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'}  ${capitalizeFirstLetter(displayTitle)}`}
              extra={(
                <FormSubmitControls
                  onFinish={justView ? null : onFinish}
                  onCancel={onCancel}
                  mutable={mutable}
                  onCancelText="Cancelar"
                  permission={permission}
                  allowSaveAndContinue={!justView}
                />
              )}
              bordered={false}
            >
              <Form
                layout="vertical"
                name="general"
                form={form}
                onFinish={() => onFinish()}
                scrollToFirstError
                initialValues={{ estados_globales: 1 }}
              >
                <Row gutter={10}>
                  {!!selectedRowKeys.length && (
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="clave"
                        label="Clave"
                        hasFeedback
                        allowClear
                      >
                        <Input disabled />
                      </Form.Item>
                    </Col>
                  )}
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="descripcion"
                      label="Descripción"
                      hasFeedback
                      allowClear
                    >
                      <Input disabled={justView} allowClear />
                    </Form.Item>
                  </Col>
                  {displayTitle === ('cedular-por-servicios-profesionales' || 'retenedor-cedular') && (
                    <>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="actividad_empresarial_especifica"
                          label="Actividad Empresarial Especifica"
                        >
                          <Input disabled={justView} allowClear />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                  {displayTitle === ('alcohol' || 'vendedor-de-alcoholes') && (
                    <>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="tipo_de_alcohol"
                          label="Tipos de Alcoholes"
                          rules={rules.required}
                        >
                          <Select
                            dataSource={tiposDeAlcoholes}
                            allowClear
                            // keyLabelRender
                            onChange={filterGiros}
                            disabled={justView}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="giro_de_alcohol"
                          label="Giro de Alcoholes"
                          rules={rules.required}
                        >
                          <Select
                            dataSource={girosDeAlcoholes}
                            allowClear
                            disabled={justView}
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                  {displayTitle === 'estacionamiento' && (
                    <>
                      <Col xs={24} sm={24} md={8}>
                        <NumericInput
                          name="numero_de_cajones"
                          label="Número de Cajones"
                          disabled={justView}
                        />
                      </Col>
                    </>
                  )}
                  {displayTitle === 'nomina' && (
                    <>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="registro_patronal_imss"
                          label="Registro Patronal IMSS"
                          rules={rules.required}
                        >
                          <Input allowClear disabled={justView} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="fecha_de_registro_patronal_imss"
                          label="Fecha Registro Patronal IMSS"
                        >
                          <DatePicker
                            disabled={justView}
                            format={DATE_FRONTEND_FORMAT}
                            disabledDate={(date) => date.isAfter(new Date())}
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="unidad_recaudadora"
                      label="Unidad Recaudadora"
                      rules={rules.required}
                    >
                      <Select disabled={justView} dataSource={unidadesDeRecaudacion} allowClear />
                    </Form.Item>
                  </Col>
                  <InputSearchPro
                    disabled={!mutable || justView}
                    label="Empresa"
                    name="q"
                    rules={rules.required}
                    onClickAdvanced={() => setVisibleBusqueda(true)}
                    breakPoints={{
                      xs: 24,
                      sm: 24,
                      md: 8,
                    }}
                    inputProps={{
                      onPressEnter: () => searchEmpresa(),
                      onChange: ({ target: { value } }) => {
                        if (!value) {
                          resetAndValidate();
                        }
                      },
                    }}
                    resultProps={{
                      breakPoints: {
                        xs: 24,
                        sm: 24,
                        md: 8,
                      },
                      name: 'empresa',
                      label: 'Empresa',
                      dataSource: empresaFound,
                      labelProp: 'nombre_comercial',
                      onClear: resetAndValidate,
                    }}
                  />
                  {displayTitle === 'alcohol' && (
                  <>
                    <Col xs={24} sm={24} md={8}>
                      <NumericInput
                        name="numero_de_permiso"
                        label="Número de Permiso"
                        required
                        disabled={justView}
                      />
                    </Col>
                  </>
                  )}
                  {displayTitle === 'alcohol' && (
                    <>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="fecha_de_vigencia_permiso"
                          label="Fecha de Vigencia"
                          hasFeedback
                        >
                          <DatePicker disabled={justView} format={DATE_FRONTEND_FORMAT} />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="estados_globales"
                      label="Estado"
                      hasFeedback
                    >
                      <Select
                        disabled={!selectedRowKeys.length || justView}
                        dataSource={estadosGlobales}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item hidden>
                  <Button htmlType="submit" />
                </Form.Item>
              </Form>
              {((displayTitle === 'alcohol' && !!selectedRowKeys.length && empresaFound?.id)) && (
                <>
                  <Col span={24}>
                    <Title level={4}>
                      <Text>
                        Establecimientos
                      </Text>
                    </Title>
                  </Col>
                  <Col span={24}>
                    <EmpresaDePadronMultiple
                      empresaID={empresaFound?.id}
                      contribuyente={empresaFound?.contribuyente?.id}
                      permission={empresasPermission}
                    />
                  </Col>
                  <Title level={4}>
                    <Text>
                      Contribuyente
                    </Text>
                  </Title>
                  <ContribuyenteDeEmpresas
                    id={empresaFound?.contribuyente?.id || null}
                  />
                </>
              )}
            </Card>
          )}
          <BusquedaAvanzadaPredio
            tipoDePadron={2}
            visible={visibleBusqueda}
            setVisible={setVisibleBusqueda}
            callback={fetchEmpresa}
            dataExterno={dataExterno}
            setDataExterno={setDataExterno}
          />
          <ModalDelete
            onDelete={deleteItem}
            onCancel={() => setVisibleAlert(false)}
            visible={visibleAlert}
            content="El registro seleccionado"
          />
        </Spin>
      </Col>
    </Row>
  );
};

export default PadronMultiple;
